import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

import type { RegistroMaestro, TablaMaestro } from './tipos'

/**
 * Datos de un maestro: leer, crear, actualizar y desactivar.
 *
 * Ningún maestro se borra. La columna 'activo' es la que se apaga: borrar un
 * cliente rompería el historial de sus órdenes, que es justamente lo que el
 * sistema existe para conservar. La base tampoco tiene política de DELETE, así
 * que el intento fallaría de todos modos.
 */

type OpcionesMaestro = {
  tabla: TablaMaestro
  /** Columna por la que se ordena la lista. */
  ordenarPor: string
  /** Incluir los desactivados. Por defecto solo se ven los activos. */
  incluirInactivos?: boolean
  /**
   * Qué columnas traer. Por defecto todas las de la tabla.
   *
   * Existe para los maestros que necesitan un dato de otra tabla: el de
   * colaboradores muestra con qué usuario entra cada quien, y eso vive en
   * perfiles porque es de la sesión, no de la nómina.
   */
  seleccion?: string
}

export function useMaestro<T extends RegistroMaestro>({
  tabla,
  ordenarPor,
  incluirInactivos = false,
  seleccion = '*',
}: OpcionesMaestro) {
  const clienteConsultas = useQueryClient()
  // La selección entra en la clave: dos pantallas que pidan columnas
  // distintas de la misma tabla no pueden compartir la respuesta guardada.
  const clave = ['maestro', tabla, incluirInactivos, seleccion] as const

  const consulta = useQuery({
    queryKey: clave,
    queryFn: async (): Promise<T[]> => {
      let peticion = supabase.from(tabla).select(seleccion).order(ordenarPor)

      if (!incluirInactivos) {
        peticion = peticion.eq('activo', true)
      }

      const { data, error } = await peticion

      if (error) throw new Error(mensajeDeError(error))

      // Único punto donde el tipado genérico de este hook y el tipado por
      // tabla del cliente de Supabase se encuentran. Es seguro porque la
      // unión 'TablaMaestro' y el tipo T los fija cada pantalla al llamar.
      return (data ?? []) as unknown as T[]
    },
  })

  /** Invalidación compartida: cualquier escritura refresca las dos variantes. */
  async function refrescar() {
    await clienteConsultas.invalidateQueries({ queryKey: ['maestro', tabla] })
  }

  const crear = useMutation({
    mutationFn: async (valores: Record<string, unknown>) => {
      const { error } = await supabase
        .from(tabla)
        // El cliente tipado espera la forma exacta de cada tabla; aquí los
        // valores vienen del formulario declarativo, ya validados por él.
        .insert(valores as never)

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })

  const actualizar = useMutation({
    mutationFn: async ({ id, valores }: { id: string; valores: Record<string, unknown> }) => {
      const { error } = await supabase
        .from(tabla)
        .update(valores as never)
        .eq('id', id)

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })

  const cambiarActivo = useMutation({
    mutationFn: async ({ id, activo }: { id: string; activo: boolean }) => {
      const { error } = await supabase
        .from(tabla)
        .update({ activo } as never)
        .eq('id', id)

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })

  return {
    registros: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
    crear,
    actualizar,
    cambiarActivo,
  }
}
